/**
 * Centralized API client for calls to the MagicIntake backend
 * (api.magicintake.com). In dev, requests go to `/api/*` which
 * Vite proxies; in prod, they go to VITE_API_BASE_URL.
 */

import { supabase } from './supabase';

const BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Make an authenticated request to the backend.
 * Automatically attaches the current Supabase access token.
 *
 * @param {string} path  — e.g. '/api/invite-member'
 * @param {object} [opts] — fetch options (method, body, etc.)
 * @returns {Promise<any>} parsed JSON response
 * @throws {Error} if the response is not OK
 */
export async function apiCall(path, opts = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers || {}),
  };

  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers,
    body: opts.body && typeof opts.body !== 'string' ? JSON.stringify(opts.body) : opts.body,
  });

  // Try to parse JSON body even on errors (backend sends { error } on 4xx).
  let data = null;
  try { data = await res.json(); } catch { /* no body */ }

  if (!res.ok) {
    const msg = data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

// Convenience wrapper. Two ways to call:
//   api('/api/foo', { team_id: 'x' })          → calls POST with that body
//   api.inviteMember({ email: 'x', team_id })  → named-method version
//   apiCall('/api/foo', { method: 'GET' })     → full control
//
// The function signature `api(path, body)` matches how most call sites are written.
export function api(path, body) {
  return apiCall(path, { method: 'POST', body });
}

// Named-method aliases (also useful for documenting the contract)
api.classifyUpload   = (body) => apiCall('/api/classify-upload',   { method: 'POST', body });
api.inviteMember     = (body) => apiCall('/api/invite-member',     { method: 'POST', body });
api.acceptInvite     = (body) => apiCall('/api/accept-invite',     { method: 'POST', body });
api.createCheckout   = (body) => apiCall('/api/create-checkout',   { method: 'POST', body });
api.billingPortal    = (body) => apiCall('/api/billing-portal',    { method: 'POST', body });
api.checkTeamLimit   = (body) => apiCall('/api/check-team-limit',  { method: 'POST', body });
api.sendNotification = (body) => apiCall('/api/send-notification', { method: 'POST', body });
api.smsOptIn         = (memberId, body) =>
  apiCall(`/api/team-members/${encodeURIComponent(memberId)}/sms-opt-in`, { method: 'POST', body });

// ── Throttled classify ─────────────────────────────────────────────────
//
// Defensive mitigation for the GPT token-limit error users hit when 20+
// files are uploaded in quick succession. If the backend has any kind of
// in-flight batching or concurrent-request bundling, slamming it with 20
// parallel classify calls can produce a single oversized OpenAI prompt.
//
// We cap concurrency to 2 and add a 250ms stagger between completions so
// the backend sees a steady stream instead of a thundering herd. This is
// a frontend-only safety net — the real fix may still need backend work.
//
// All callers should prefer `api.classifyUploadThrottled` over `api.classifyUpload`
// for the post-upload classify ping.
function createLimiter(maxConcurrent, staggerMs) {
  let active = 0;
  const queue = [];

  const runNext = () => {
    if (active >= maxConcurrent || queue.length === 0) return;
    const { fn, resolve, reject } = queue.shift();
    active++;
    Promise.resolve()
      .then(fn)
      .then(
        (v) => { resolve(v); release(); },
        (e) => { reject(e); release(); }
      );
  };

  const release = () => {
    active--;
    if (staggerMs > 0 && queue.length > 0) setTimeout(runNext, staggerMs);
    else runNext();
  };

  return (fn) => new Promise((resolve, reject) => {
    queue.push({ fn, resolve, reject });
    runNext();
  });
}

const classifyLimiter = createLimiter(2, 250);

api.classifyUploadThrottled = (body) =>
  classifyLimiter(() => apiCall('/api/classify-upload', { method: 'POST', body }));
